import {
  DateStampString,
  EditableFrequency,
  MonthlyFrequency,
  Schedule,
  TwiceMonthlyFrequency,
  WeeklyFrequency,
} from "source/giving/donate/form/types"
import {
  scheduleToEditableFrequency,
  FrequencyTypeOptions,
  WeekdayOptions,
  MonthDayOptions,
  TwiceMonthlyOptions,
  setInitialFrequencyValues,
} from "../utils/frequency"
import { Select } from "@planningcenter/doxy-web"
import { frequencyToSchedule } from "source/giving/donate/form/utils/frequency"
import { useState } from "react"

interface EditFrequencyProps {
  schedule: Schedule
  nextOccurrenceDate: DateStampString
  onChangeFrequency: (schedule: Schedule) => void
}

export default function EditFrequency({
  schedule,
  nextOccurrenceDate,
  onChangeFrequency,
}: EditFrequencyProps) {
  const [editableFrequency, setEditableFrequency] = useState<EditableFrequency>(
    scheduleToEditableFrequency(schedule, nextOccurrenceDate),
  )

  const handleFrequencyTypeChange = (newFrequency: EditableFrequency) => {
    setInitialFrequencyValues(newFrequency)
    handleFrequencyDaysChange(newFrequency)
  }

  const handleFrequencyDaysChange = (newFrequency: EditableFrequency) => {
    setEditableFrequency(newFrequency)
    onChangeFrequency(frequencyToSchedule(newFrequency))
  }

  function frequencyPreposition(frequency: EditableFrequency): string {
    if (frequency.type === "weekly" || frequency.type === "every_other_week") {
      return "on"
    } else {
      return "on the"
    }
  }

  return (
    <div className="my-2 d-f@md fw-w fw-n@md ai-c">
      <div className="f-1">
        <Select
          id="frequency-type"
          value={editableFrequency.type}
          onChange={(newValue) => {
            const newFrequency = {
              ...editableFrequency,
              type: newValue.target.value as EditableFrequency["type"],
            }
            handleFrequencyTypeChange(newFrequency as EditableFrequency)
          }}
          aria-label="recurrence frequency options"
        >
          {FrequencyTypeOptions.map((frequency, index) => {
            return (
              <Select.Option
                key={index}
                value={frequency.value}
                label={frequency.label}
              />
            )
          })}
        </Select>
      </div>
      <div className="p-1@md py-1">
        <span className="text">{frequencyPreposition(editableFrequency)}</span>
      </div>
      <div className="f-1">
        {editableFrequency.type === "weekly" ||
        editableFrequency.type === "every_other_week" ? (
          <Select
            id="frequency-weekday"
            value={editableFrequency.weekday?.toString()}
            onChange={(e) =>
              handleFrequencyDaysChange({
                ...editableFrequency,
                weekday: Number(e.target.value),
              } as WeeklyFrequency)
            }
            aria-label="weekday for donation"
          >
            {WeekdayOptions.map(({ value, label }) => {
              return <Select.Option key={value} value={value} label={label} />
            })}
          </Select>
        ) : editableFrequency.type === "monthly" ? (
          <Select
            id="frequency-monthly-day"
            value={editableFrequency.day?.toString()}
            onChange={(e) =>
              handleFrequencyDaysChange({
                ...editableFrequency,
                day: Number(e.target.value),
              } as MonthlyFrequency)
            }
            aria-label="day of month for donation"
          >
            {MonthDayOptions.map(({ value, label }) => (
              <Select.Option key={value} value={value} label={label} />
            ))}
          </Select>
        ) : editableFrequency.type === "twice_monthly" ? (
          <Select
            id="frequency-twice-monthly-days"
            value={editableFrequency.days}
            onChange={(e) =>
              handleFrequencyDaysChange({
                ...editableFrequency,
                days: e.target.value,
              } as TwiceMonthlyFrequency)
            }
            aria-label="days of month for donation"
          >
            {TwiceMonthlyOptions.map(({ value, label }) => (
              <Select.Option key={value} value={value} label={label} />
            ))}
          </Select>
        ) : null}
      </div>
    </div>
  )
}
